/**
 * Rate Limit Manager
 * Tracks API rate limits from response headers and manages request queuing
 */

class RateLimitManager {
  constructor() {
    this.remaining = 60 // Default to 60 requests per minute
    this.resetTime = null
    this.requestQueue = []
    this.processingQueue = false
    this.maxRequestsPerMinute = 60
    this.warningThreshold = 10 // Queue requests when below this
  }

  /**
   * Updates rate limit information from response headers
   * @param {Object} headers - HTTP response headers
   */
  updateFromHeaders(headers) {
    // Parse rate limit headers (common formats)
    const remaining = headers['x-ratelimit-remaining'] || 
                      headers['X-RateLimit-Remaining'] ||
                      headers['ratelimit-remaining']
    
    const reset = headers['x-ratelimit-reset'] || 
                  headers['X-RateLimit-Reset'] ||
                  headers['ratelimit-reset']

    if (remaining !== undefined && remaining !== null) {
      this.remaining = parseInt(remaining, 10)
    }

    if (reset !== undefined && reset !== null) {
      // Reset time could be a timestamp (seconds) or milliseconds
      const resetTimestamp = parseInt(reset, 10)
      // If it's less than current timestamp in seconds, assume it's seconds
      // Otherwise assume milliseconds
      this.resetTime = resetTimestamp < Date.now() / 1000 
        ? resetTimestamp * 1000 
        : resetTimestamp
    }
  }

  /**
   * Checks if we can make a request now
   * @returns {boolean}
   */
  canMakeRequest() {
    // If we don't have reset time info, allow requests but be conservative
    if (!this.resetTime) {
      return this.remaining > this.warningThreshold
    }

    // Check if reset time has passed
    if (Date.now() >= this.resetTime) {
      // Reset time has passed, reset the counter
      this.remaining = this.maxRequestsPerMinute
      this.resetTime = null
      return true
    }

    // Check if we have remaining requests
    return this.remaining > 0
  }

  /**
   * Gets the time until rate limit resets (in milliseconds)
   * @returns {number}
   */
  getTimeUntilReset() {
    if (!this.resetTime) {
      return 0
    }
    const timeUntilReset = this.resetTime - Date.now()
    return Math.max(0, timeUntilReset)
  }

  /**
   * Decrements the remaining request count
   */
  decrement() {
    if (this.remaining > 0) {
      this.remaining--
    }
  }

  /**
   * Queues a request to be executed when rate limit allows
   * @param {Function} requestFn - Function that returns a promise for the request
   * @param {number} priority - Priority level (higher = more important, default 0)
   * @returns {Promise}
   */
  queueRequest(requestFn, priority = 0) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({
        requestFn,
        priority,
        resolve,
        reject,
        timestamp: Date.now()
      })

      // Sort queue by priority (higher first), then by timestamp
      this.requestQueue.sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority
        }
        return a.timestamp - b.timestamp
      })

      // Start processing queue if not already processing
      if (!this.processingQueue) {
        this.processQueue()
      }
    })
  }

  /**
   * Processes the request queue
   */
  async processQueue() {
    if (this.processingQueue) {
      return
    }

    this.processingQueue = true

    while (this.requestQueue.length > 0) {
      // Wait if we can't make requests
      if (!this.canMakeRequest()) {
        const waitTime = this.getTimeUntilReset()
        if (waitTime > 0) {
          await new Promise(resolve => setTimeout(resolve, waitTime + 100)) // Add 100ms buffer
        }
      }

      // Process next request
      const item = this.requestQueue.shift()
      if (item) {
        try {
          this.decrement()
          const result = await item.requestFn()
          item.resolve(result)
        } catch (error) {
          item.reject(error)
        }
      }
    }

    this.processingQueue = false
  }

  /**
   * Gets current rate limit status
   * @returns {Object}
   */
  getStatus() {
    return {
      remaining: this.remaining,
      resetTime: this.resetTime,
      timeUntilReset: this.getTimeUntilReset(),
      queuedRequests: this.requestQueue.length,
      canMakeRequest: this.canMakeRequest()
    }
  }

  /**
   * Resets rate limit manager (useful for testing or manual reset)
   */
  reset() {
    this.remaining = this.maxRequestsPerMinute
    this.resetTime = null
    this.requestQueue = []
    this.processingQueue = false
  }
}

// Export singleton instance
export const rateLimitManager = new RateLimitManager()
